<template>
  <v-main>
    <v-container fluid>
      <!-- Delivery Dashboard -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-2 pt-3 px-4">
              <v-icon class="mr-2" color="primary">mdi-view-dashboard-outline</v-icon>
              <span class="text-h6 font-weight-bold primary--text">Delivery Dashboard</span>
            </v-card-title>
            <v-divider class="mx-4"></v-divider>
            <v-card-text class="px-4 pb-4">
              <!-- Stat Cards -->
              <v-row dense>
                <v-col cols="6" sm="4" md="3" class="stat-col-5" v-for="card in statCards" :key="card.title">
                  <div :style="card.bucket ? 'cursor:pointer' : ''" @click="card.bucket && openBucketDialog(card)">
                    <DashboardWidget
                      dense
                      :title="card.title"
                      :value="card.value"
                      :icon="card.icon"
                      :color="card.color"
                      :loading="isDashboardLoading"
                      :value-type="card.valueType || 'number'"
                      :subtitle="card.subtitle"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Charts -->
              <v-row dense class="mt-2">
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">DAILY SHIPMENTS (PICKED)</div>
                    <div style="height:220px;position:relative;">
                      <canvas ref="dailyShipmentsChart"></canvas>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">STATE-WISE DELIVERY</div>
                    <div style="height:220px;position:relative;">
                      <canvas ref="stateWiseChart"></canvas>
                    </div>
                  </v-card>
                </v-col>

                <!-- Courier Performance -->
                <v-col cols="12">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center justify-space-between mb-2 flex-wrap">
                      <div class="d-flex align-center">
                        <span class="text-caption font-weight-bold grey--text text--darken-1">COURIER PERFORMANCE</span>
                        <v-tooltip bottom max-width="360" open-delay="100">
                          <template #activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey">mdi-information-outline</v-icon>
                          </template>
                          <div class="py-1">
                            <div class="font-weight-bold mb-1">How performance is calculated</div>
                            <div class="mb-2">Couriers are ranked by <b>total deliveries completed</b> (highest first).</div>
                            <div class="font-weight-bold mb-1">Factors considered</div>
                            <ul class="pl-4 mb-2">
                              <li><b>Success rate</b> (delivered ÷ total shipments) — the primary factor</li>
                              <li><b>RTO / Returned / Cancelled rate</b> — secondary, shown as failure rate</li>
                              <li><b>Avg delivery time</b> — hours from shipment creation to delivery</li>
                              <li><b>Avg shipping cost</b> — for cost-efficiency comparison</li>
                            </ul>
                          </div>
                        </v-tooltip>
                      </div>
                      <div class="text-caption grey--text">
                        Ranked by deliveries completed — success rate is the primary quality signal, RTO/cancelled rate and average delivery time are secondary factors.
                      </div>
                    </div>
                    <v-data-table
                      :headers="courierHeaders"
                      :items="courierPerformance"
                      :items-per-page="-1"
                      hide-default-footer
                      dense
                      no-data-text="No courier shipment data for the selected filters"
                    >
                      <template #[`item.rank`]="{ index }">
                        <v-icon v-if="index === 0 && courierPerformance.length > 1" color="amber darken-2" small>mdi-crown</v-icon>
                        <span v-else>{{ index + 1 }}</span>
                      </template>
                      <template #[`item.courier`]="{ item }">
                        <span class="font-weight-medium">{{ item.courier }}</span>
                      </template>
                      <template #[`item.success_rate`]="{ item }">
                        <div class="d-flex align-center" style="min-width:110px">
                          <v-progress-linear
                            :value="item.success_rate"
                            :color="item.success_rate >= 85 ? 'success' : item.success_rate >= 60 ? 'info' : item.success_rate >= 40 ? 'warning' : 'error'"
                            height="6"
                            rounded
                            class="mr-2"
                            style="flex:1"
                          ></v-progress-linear>
                          <span style="min-width:36px">{{ item.success_rate }}%</span>
                        </div>
                      </template>
                      <template #[`item.failure_rate`]="{ item }">
                        <span :class="item.failure_rate > 0 ? 'error--text' : ''">{{ item.failure_rate }}%</span>
                      </template>
                      <template #[`item.avg_delivery_hours`]="{ item }">
                        {{ item.avg_delivery_hours !== null ? item.avg_delivery_hours + " hrs" : "—" }}
                      </template>
                      <template #[`item.avg_shipping_cost`]="{ item }">
                        ₹{{ item.avg_shipping_cost }}
                      </template>
                    </v-data-table>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-card outlined class="pa-3">
                    <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">SHIPPING COST TRENDS</div>
                    <div style="height:220px;position:relative;">
                      <canvas ref="shippingCostChart"></canvas>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Header + Filters Card -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Deliveries"
              subtitle="View and monitor all order deliveries"
            >
            </page-header>

            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Deliveries</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip v-if="hasActiveFilters" color="info" x-small class="mr-2">
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn text x-small color="error" @click="clearAllFilters" :disabled="!hasActiveFilters">
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Deliveries"
                    prepend-inner-icon="mdi-magnify"
                    outlined dense clearable
                    placeholder="AWB number, courier, status..."
                    hide-details
                    @click:clear="(filter.search_keyword = null), refreshAll()"
                    @keydown.enter="refreshAll()"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Status"
                    prepend-inner-icon="mdi-package-variant"
                    outlined dense clearable hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="activeOptions"
                    label="Active"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined dense clearable hide-details
                    @change="refreshAll()"
                  ></v-autocomplete>
                </v-col>

                <!-- From Date -->
                <v-col cols="12" md="2">
                  <v-menu v-model="fromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.start_date"
                        label="From Date"
                        prepend-inner-icon="mdi-calendar"
                        outlined dense clearable hide-details readonly
                        v-bind="attrs" v-on="on"
                        @click:clear="(filter.start_date = ''), refreshAll()"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="filter.start_date" no-title @input="fromMenu = false; refreshAll()"></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- To Date -->
                <v-col cols="12" md="2">
                  <v-menu v-model="toMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.end_date"
                        label="To Date"
                        prepend-inner-icon="mdi-calendar"
                        outlined dense clearable hide-details readonly
                        v-bind="attrs" v-on="on"
                        @click:clear="(filter.end_date = ''), refreshAll()"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="filter.end_date" no-title @input="toMenu = false; refreshAll()"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-table</v-icon>
                    <span class="text-body-1 font-weight-medium">Deliveries List</span>
                  </div>
                </v-col>
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-truck-fast</v-icon>
                      {{ count }} {{ count === 1 ? "Delivery" : "Deliveries" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn small color="success" class="mr-3" :loading="isExcelLoading" @click="exportExcel">
                      <v-icon left small>mdi-microsoft-excel</v-icon>
                      Export Excel
                    </v-btn>
                    <span class="text-caption grey--text mr-1">Show:</span>
                    <v-btn-toggle v-model="selectedItemsPerPage" dense class="mr-2" @change="changeItemsPerPage">
                      <v-btn x-small outlined :value="10">10</v-btn>
                      <v-btn x-small outlined :value="25">25</v-btn>
                      <v-btn x-small outlined :value="50">50</v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-card-text class="pa-0">
              <section v-if="isLoading" style="height:400px" class="d-flex align-center justify-center">
                <div class="text-center">
                  <health-loader message="Loading deliveries..." />
                </div>
              </section>

              <v-data-table
                v-else
                :headers="headers"
                :items="deliveries"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                show-expand
                :expanded.sync="expanded"
                item-key="id"
                class="enhanced-table"
              >
                <!-- Serial # -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- AWB Number -->
                <template #[`item.awb_number`]="{ item }">
                  <v-chip x-small outlined color="primary" class="font-weight-medium">
                    {{ item.awb_number || '—' }}
                  </v-chip>
                </template>

                <!-- Order ID -->
                <template #[`item.order_id`]="{ item }">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ item.order ? item.order.order_number : (item.order_id ? '#' + item.order_id : '—') }}</div>
                    <div class="text-caption grey--text" v-if="item.order && item.order.delivery_charges">
                      Delivery: ₹{{ item.order.delivery_charges | toFixed2 }}
                    </div>
                  </div>
                </template>

                <!-- Seller -->
                <template #[`item.seller`]="{ item }">
                  <span class="text-body-2">{{ (item.order && item.order.seller_store_name) || '—' }}</span>
                </template>

                <!-- Customer -->
                <template #[`item.customer`]="{ item }">
                  <span class="text-body-2">{{ (item.order && item.order.customer && item.order.customer.user_name) || '—' }}</span>
                </template>

                <!-- Courier Info -->
                <template #[`item.courier`]="{ item }">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ item.courier_name || '—' }}</div>
                    <div class="text-caption grey--text" v-if="item.courier_id">ID: {{ item.courier_id }}</div>
                  </div>
                </template>

                <!-- Payment Type -->
                <template #[`item.payment_type`]="{ item }">
                  <span class="text-body-2 text-capitalize">{{ item.payment_type || '—' }}</span>
                </template>

                <!-- Order Value -->
                <template #[`item.order_value`]="{ item }">
                  <span class="text-body-2" v-if="item.order && item.order.total_amount">₹{{ item.order.total_amount | toFixed2 }}</span>
                  <span class="text-body-2" v-else>—</span>
                </template>

                <!-- Delivery Charge -->
                <template #[`item.delivery_charge`]="{ item }">
                  <span class="text-body-2" v-if="item.order && item.order.delivery_charges">₹{{ item.order.delivery_charges | toFixed2 }}</span>
                  <span class="text-body-2" v-else>—</span>
                </template>

                <!-- Created On -->
                <template #[`item.created_on`]="{ item }">
                  <div>
                    <div class="text-body-2">{{ item.created_at | formatDate }}</div>
                    <div class="text-caption grey--text">{{ item.created_at | formatTime }}</div>
                  </div>
                </template>

                <!-- Status -->
                <template #[`item.status`]="{ item }">
                  <v-chip x-small :color="getStatusColor(item.status)" :text-color="getStatusTextColor(item.status)" class="text-capitalize font-weight-medium">
                    {{ item.status || 'Unknown' }}
                  </v-chip>
                </template>

                <!-- Order Status (order's own lifecycle status, may differ from shipment status) -->
                <template #[`item.order_status_col`]="{ item }">
                  <v-chip x-small outlined :color="getOrderStatusColor(item.order && item.order.order_status)">
                    {{ getOrderStatusLabel(item.order && item.order.order_status) }}
                  </v-chip>
                </template>

                <!-- Last Tracking Update (from third-party courier tracking history) -->
                <template #[`item.last_tracking`]="{ item }">
                  <div v-if="item.order && item.order.latest_tracking">
                    <div class="text-body-2 font-weight-medium">{{ item.order.latest_tracking.message || '—' }}</div>
                    <div class="text-caption grey--text">{{ item.order.latest_tracking.location || '—' }}</div>
                    <div class="text-caption grey--text" v-if="item.order.latest_tracking.event_time">{{ item.order.latest_tracking.event_time | formatDate }} {{ item.order.latest_tracking.event_time | formatTime }}</div>
                  </div>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Invoice No -->
                <template #[`item.invoice_no`]="{ item }">
                  <span class="text-body-2">{{ (item.order && item.order.invoice_no) || '—' }}</span>
                </template>

                <!-- Cancellation / Refund -->
                <template #[`item.cancellation_refund`]="{ item }">
                  <div v-if="item.order && (item.order.cancellation_reason || item.order.refund_status)">
                    <div class="text-caption" v-if="item.order.cancellation_reason">
                      <v-icon x-small color="error" class="mr-1">mdi-cancel</v-icon>{{ item.order.cancellation_reason }}
                    </div>
                    <div class="text-caption" v-if="item.order.refund_status">
                      <v-icon x-small color="info" class="mr-1">mdi-cash-refund</v-icon>
                      {{ item.order.refund_status | capitalize }}
                      <span v-if="item.order.refund_amount">(₹{{ item.order.refund_amount | toFixed2 }})</span>
                    </div>
                  </div>
                  <span v-else class="grey--text">—</span>
                </template>


                <!-- Shipment ID -->
                <template #[`item.shipment_id`]="{ item }">
                  <span class="text-caption">{{ item.shipment_id || '—' }}</span>
                </template>

                <!-- Weight -->
                <template #[`item.weight`]="{ item }">
                  <div v-if="item.order && item.order.order_items && item.order.order_items.length">
                    <span class="text-body-2 font-weight-medium">
                      {{ getTotalWeight(item) }}
                    </span>
                    <div class="text-caption grey--text">g</div>
                  </div>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Additional Info -->
                <template #[`item.additional_info`]="{ item }">
                  <v-tooltip bottom v-if="item.additional_info" max-width="300">
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="text-caption d-inline-block text-truncate" style="max-width:180px;">
                        {{ item.additional_info }}
                      </span>
                    </template>
                    <span>{{ item.additional_info }}</span>
                  </v-tooltip>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Active Status -->
                <template #[`item.is_active`]="{ item }">
                  <status-chip x-small :color="item.is_active ? 'success' : 'error'" :label="String(item.is_active ? 'Active' : 'Inactive')" />
                </template>

                <!-- Expanded detail panel -->
                <template #expanded-item="{ headers: h, item }">
                  <td :colspan="h.length" class="pa-0">
                    <v-sheet class="pa-4" color="grey lighten-5">
                      <v-row dense>
                        <!-- From Address -->
                        <v-col cols="12" md="4">
                          <v-card outlined class="pa-3 h-100">
                            <div class="d-flex align-center mb-2">
                              <v-icon small color="blue darken-2" class="mr-1">mdi-store</v-icon>
                              <span class="text-caption font-weight-bold blue--text text--darken-2">FROM (Seller / Pickup)</span>
                            </div>
                            <template v-if="item.order">
                              <div class="text-body-2 font-weight-medium">{{ item.order.seller_store_name || '—' }}</div>
                              <div class="text-caption grey--text mt-1">{{ item.order.seller_store_address || '—' }}</div>
                              <div class="text-caption grey--text">{{ [item.order.seller_store_city, item.order.seller_store_state, item.order.seller_store_pincode].filter(Boolean).join(', ') || '—' }}</div>
                              <div class="text-caption grey--text" v-if="item.order.seller_store_phone">📞 {{ item.order.seller_store_phone }}</div>
                            </template>
                            <span v-else class="text-caption grey--text">No address data</span>
                          </v-card>
                        </v-col>

                        <!-- To Address -->
                        <v-col cols="12" md="4">
                          <v-card outlined class="pa-3 h-100">
                            <div class="d-flex align-center mb-2">
                              <v-icon small color="green darken-2" class="mr-1">mdi-map-marker</v-icon>
                              <span class="text-caption font-weight-bold green--text text--darken-2">TO (Customer / Delivery)</span>
                            </div>
                            <template v-if="item.order">
                              <div class="text-caption grey--text mt-1">{{ item.order.customer_address || '—' }}</div>
                              <div class="text-caption grey--text">{{ [item.order.customer_city, item.order.customer_state, item.order.customer_pincode].filter(Boolean).join(', ') || '—' }}</div>
                              <div class="text-caption grey--text" v-if="item.order.customer_phone">📞 {{ item.order.customer_phone }}</div>
                            </template>
                            <span v-else class="text-caption grey--text">No address data</span>
                          </v-card>
                        </v-col>

                        <!-- Products & Dimensions -->
                        <v-col cols="12" md="4">
                          <v-card outlined class="pa-3 h-100">
                            <div class="d-flex align-center mb-2">
                              <v-icon small color="orange darken-2" class="mr-1">mdi-package-variant</v-icon>
                              <span class="text-caption font-weight-bold orange--text text--darken-2">PRODUCTS & DIMENSIONS</span>
                            </div>
                            <template v-if="item.order && item.order.order_items && item.order.order_items.length">
                              <div v-for="(oi, idx) in item.order.order_items" :key="idx" class="mb-2">
                                <div class="text-caption font-weight-medium">
                                  {{ getProductName(oi) }} × {{ oi.quantity }}
                                </div>
                                <div class="text-caption grey--text">
                                  <span v-if="getVariant(oi)">
                                    W: {{ getVariant(oi).weight || '—' }} kg &nbsp;|&nbsp;
                                    {{ getVariant(oi).length || '—' }} × {{ getVariant(oi).width || '—' }} × {{ getVariant(oi).height || '—' }} cm
                                  </span>
                                </div>
                              </div>
                              <v-divider class="my-1"></v-divider>
                              <div class="text-caption font-weight-medium">
                                Delivery Charges: <strong>₹{{ item.order.delivery_charges | toFixed2 }}</strong> &nbsp;|&nbsp;
                                Order Total: <strong>₹{{ item.order.total_amount | toFixed2 }}</strong>
                              </div>
                            </template>
                            <span v-else class="text-caption grey--text">No product data</span>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </td>
                </template>

                <!-- Empty state -->
                <template #no-data>
                  <div class="text-center py-8">
                    <v-icon size="64" color="grey lighten-2">mdi-truck-fast-outline</v-icon>
                    <p class="mt-3 grey--text text-body-1">
                      {{ hasActiveFilters ? 'No deliveries match your filters.' : 'No deliveries found.' }}
                    </p>
                    <v-btn v-if="hasActiveFilters" text color="primary" small @click="clearAllFilters">
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Pagination Footer -->
            <v-card-actions class="px-4 py-3" v-if="!isLoading && count > 0">
              <v-row align="center">
                <v-col cols="12" md="6">
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    color="primary"
                    circle
                  ></v-pagination>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      outlined dense hide-details
                      style="max-width:70px"
                      @keydown.enter="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Bucket Drill-down Dialog -->
    <v-dialog v-model="bucketDialog" max-width="900">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon :color="bucketDialogCard.color" class="mr-2">{{ bucketDialogCard.icon }}</v-icon>
          <span>{{ bucketDialogCard.title }} Deliveries</span>
          <v-chip x-small class="ml-2" outlined>{{ bucketDialogItems.length }}</v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="bucketDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="bucketDialogHeaders"
            :items="bucketDialogItems"
            :items-per-page="10"
            :loading="isBucketDialogLoading"
            dense
            no-data-text="No deliveries found for this status"
          >
            <template #[`item.order_amount`]="{ item }">
              ₹{{ parseFloat(item.order_amount || 0).toFixed(2) }}
            </template>
            <template #[`item.awb_number`]="{ item }">
              {{ item.awb_number || "—" }}
            </template>
            <template #[`item.seller_name`]="{ item }">
              {{ item.seller_name || "—" }}
            </template>
            <template #[`item.courier_name`]="{ item }">
              {{ item.courier_name || "—" }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";
import Chart from "chart.js/auto";
import DashboardWidget from "@/components/DashboardWidget.vue";

export default {
  components: { DashboardWidget },
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
        is_active: "",
        start_date: "",
        end_date: "",
      },
      fromMenu: false,
      toMenu: false,
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      paginationLength: 0,
      count: 0,
      jumpToPage: null,
      deliveries: [],
      isLoading: false,
      isExcelLoading: false,
      expanded: [],
      statusOptions: [
        { text: "Failed", value: "failed" },
        { text: "Delivered", value: "delivered" },
        { text: "In Transit", value: "in_transit" },
        { text: "Pending", value: "pending" },
        { text: "Cancelled", value: "cancelled" },
        { text: "Returned", value: "returned" },
      ],
      activeOptions: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px", align: "center" },
        { text: "Shipment ID", value: "shipment_id", sortable: false, width: "110px" },
        { text: "AWB Number", value: "awb_number", sortable: false, width: "160px" },
        { text: "Order No", value: "order_id", sortable: false, width: "120px" },
        { text: "Seller", value: "seller", sortable: false, width: "150px" },
        { text: "Customer", value: "customer", sortable: false, width: "140px" },
        { text: "Courier", value: "courier", sortable: false, width: "150px" },
        { text: "Status", value: "status", sortable: false, width: "120px" },
        { text: "Order Status", value: "order_status_col", sortable: false, width: "130px" },
        { text: "Last Tracking Update", value: "last_tracking", sortable: false, width: "220px" },
        { text: "Payment Type", value: "payment_type", sortable: false, width: "120px" },
        { text: "Order Value", value: "order_value", sortable: false, width: "110px" },
        { text: "Delivery Charge", value: "delivery_charge", sortable: false, width: "120px" },
        { text: "Invoice No", value: "invoice_no", sortable: false, width: "150px" },
        { text: "Cancellation / Refund", value: "cancellation_refund", sortable: false, width: "220px" },
        { text: "Created On", value: "created_on", sortable: false, width: "130px" },
        { text: "Weight", value: "weight", sortable: false, width: "90px" },
        { text: "Additional Info", value: "additional_info", sortable: false, width: "200px" },
        { text: "Active", value: "is_active", sortable: false, width: "80px" },
      ],
      isDashboardLoading: false,
      dashboardStats: {},
      charts: { daily: null, state: null, cost: null },
      bucketDialog: false,
      bucketDialogCard: {},
      bucketDialogItems: [],
      isBucketDialogLoading: false,
      bucketDialogHeaders: [
        { text: "AWB", value: "awb_number", sortable: false },
        { text: "Order Number", value: "order_number", sortable: false },
        { text: "Seller Name", value: "seller_name", sortable: false },
        { text: "Courier Name", value: "courier_name", sortable: false },
        { text: "Order Amount", value: "order_amount", sortable: false, align: "end" },
      ],
      courierPerformance: [],
      courierHeaders: [
        { text: "#", value: "rank", sortable: false, width: "50px" },
        { text: "Courier", value: "courier", sortable: false },
        { text: "Total Shipments", value: "total_shipments", sortable: false, align: "center" },
        { text: "Delivered", value: "delivered", sortable: false, align: "center" },
        { text: "RTO", value: "rto", sortable: false, align: "center" },
        { text: "Returned", value: "returned", sortable: false, align: "center" },
        { text: "Cancelled", value: "cancelled", sortable: false, align: "center" },
        { text: "Success Rate", value: "success_rate", sortable: false },
        { text: "RTO/Cancelled Rate", value: "failure_rate", sortable: false, align: "center" },
        { text: "Avg Delivery Time", value: "avg_delivery_hours", sortable: false, align: "center" },
        { text: "Avg Shipping Cost", value: "avg_shipping_cost", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(this.filter.search_keyword || this.filter.status || this.filter.is_active || this.filter.start_date || this.filter.end_date);
    },
    statCards() {
      const s = this.dashboardStats;
      return [
        { title: "Total Shipments", value: s.total_shipments || 0, icon: "mdi-package-variant-closed", color: "primary" },
        { title: "Pending", value: s.pending || 0, icon: "mdi-clock-outline", color: "warning", bucket: "pending" },
        { title: "Picked Up", value: s.picked_up || 0, icon: "mdi-hand-back-right", color: "info", bucket: "picked_up" },
        { title: "In Transit", value: s.in_transit || 0, icon: "mdi-truck-fast", color: "info", bucket: "in_transit" },
        { title: "Out For Delivery", value: s.out_for_delivery || 0, icon: "mdi-truck-delivery", color: "secondary", bucket: "out_for_delivery" },
        { title: "Delivered", value: s.delivered || 0, icon: "mdi-check-circle", color: "success", bucket: "delivered" },
        { title: "Returned", value: s.returned || 0, icon: "mdi-keyboard-return", color: "error", bucket: "returned" },
        { title: "RTO", value: s.rto || 0, icon: "mdi-undo-variant", color: "error", bucket: "rto" },
        { title: "Avg Delivery Time", value: s.avg_delivery_time_hours || 0, icon: "mdi-timer-sand", color: "primary", subtitle: "hours" },
        { title: "Total Shipping Cost", value: s.total_shipping_cost || 0, icon: "mdi-currency-inr", color: "success", valueType: "currency" },
      ];
    },
  },
  watch: {
    page: "getData",
    itemsPerPage: "getData",
  },
  mounted() {
    this.refreshAll();
  },
  beforeDestroy() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    refreshAll() {
      this.page = 1;
      this.getData();
      this.fetchDashboard();
    },
    async getData() {
      this.isLoading = true;
      const query = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status || "",
        is_active: this.filter.is_active || "",
        start_date: this.filter.start_date || "",
        end_date: this.filter.end_date || "",
      }).toString();

      await axios
        .get(`deliveries?${query}`)
        .then((response) => {
          this.deliveries = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch((error) => {
          console.error(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    async fetchDashboard() {
      this.isDashboardLoading = true;
      const query = new URLSearchParams({
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status || "",
        is_active: this.filter.is_active || "",
        start_date: this.filter.start_date || "",
        end_date: this.filter.end_date || "",
      }).toString();

      try {
        const { data } = await axios.get(`deliveries/dashboard?${query}`);
        this.dashboardStats = data.data.stats;
        this.courierPerformance = data.data.charts.courier_performance || [];
        this.$nextTick(() => this.renderCharts(data.data.charts));
      } catch (error) {
        console.error("Failed to load delivery dashboard:", error);
      }
      this.isDashboardLoading = false;
    },

    async openBucketDialog(card) {
      this.bucketDialogCard = card;
      this.bucketDialog = true;
      this.bucketDialogItems = [];
      this.isBucketDialogLoading = true;

      const query = new URLSearchParams({
        bucket: card.bucket,
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status || "",
        is_active: this.filter.is_active || "",
        start_date: this.filter.start_date || "",
        end_date: this.filter.end_date || "",
      }).toString();

      try {
        const { data } = await axios.get(`deliveries/dashboard/list?${query}`);
        this.bucketDialogItems = data.data || [];
      } catch (error) {
        console.error("Failed to load delivery bucket list:", error);
      }
      this.isBucketDialogLoading = false;
    },

    renderCharts(charts) {
      Object.values(this.charts).forEach((c) => c && c.destroy());

      if (this.$refs.dailyShipmentsChart) {
        this.charts.daily = new Chart(this.$refs.dailyShipmentsChart.getContext("2d"), {
          type: "line",
          data: {
            labels: charts.daily_shipments.map((r) => r.date),
            datasets: [{ label: "Picked", data: charts.daily_shipments.map((r) => r.count), borderColor: "#1976D2", backgroundColor: "rgba(25,118,210,0.1)", tension: 0.4, fill: true }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
        });
      }

      if (this.$refs.stateWiseChart) {
        this.charts.state = new Chart(this.$refs.stateWiseChart.getContext("2d"), {
          type: "doughnut",
          data: {
            labels: charts.state_wise_delivery.map((r) => r.state),
            datasets: [{ data: charts.state_wise_delivery.map((r) => r.count), backgroundColor: ["#2196F3", "#FF9800", "#9C27B0", "#4CAF50", "#F44336", "#607D8B", "#00BCD4", "#795548"] }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right", labels: { boxWidth: 10, font: { size: 10 } } } } },
        });
      }

      if (this.$refs.shippingCostChart) {
        this.charts.cost = new Chart(this.$refs.shippingCostChart.getContext("2d"), {
          type: "bar",
          data: {
            labels: charts.shipping_cost_trends.map((r) => r.date),
            datasets: [{ label: "Shipping Cost (₹)", data: charts.shipping_cost_trends.map((r) => r.total), backgroundColor: "#FF9800" }],
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
        });
      }
    },

    getActiveFiltersCount() {
      return [this.filter.search_keyword, this.filter.status, this.filter.is_active, this.filter.start_date, this.filter.end_date].filter(Boolean).length;
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.status = "";
      this.filter.is_active = "";
      this.filter.start_date = "";
      this.filter.end_date = "";
      this.refreshAll();
    },

    async exportExcel() {
      this.isExcelLoading = true;
      try {
        const params = new URLSearchParams({
          search_keyword: this.filter.search_keyword || "",
          status: this.filter.status || "",
          is_active: this.filter.is_active || "",
          start_date: this.filter.start_date || "",
          end_date: this.filter.end_date || "",
        }).toString();
        const response = await axios.get(`deliveries/excel?${params}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `deliveries_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export failed:", error);
      }
      this.isExcelLoading = false;
    },

    changeItemsPerPage() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const target = parseInt(this.jumpToPage);
      if (target && target >= 1 && target <= this.paginationLength) {
        this.page = target;
        this.getData();
      }
      this.jumpToPage = null;
    },

    getStatusColor(status) {
      const map = {
        delivered: "success",
        failed: "error",
        in_transit: "info",
        pending: "warning",
        cancelled: "grey",
        returned: "orange",
      };
      return map[(status || "").toLowerCase()] || "grey";
    },

    getStatusTextColor(status) {
      const light = ["warning", "grey"];
      return light.includes(this.getStatusColor(status)) ? "black" : "white";
    },

    // Order.order_status: 0=Pending,1=Processing,2=Packed,3=OutForPickup,4=InTransit,
    // 5=OutForDelivery,6=Delivered,7=Exception,8=RTO,9=RTOInTransit,10=RTODelivered,
    // 11=Cancelled,12=PaymentFailed
    orderStatusLabels() {
      return {
        0: "Pending", 1: "Processing", 2: "Packed", 3: "Out For Pickup",
        4: "In Transit", 5: "Out For Delivery", 6: "Delivered", 7: "Exception",
        8: "RTO", 9: "RTO In Transit", 10: "RTO Delivered", 11: "Cancelled", 12: "Payment Failed",
      };
    },

    getOrderStatusLabel(status) {
      if (status === null || status === undefined || status === "") return "—";
      return this.orderStatusLabels()[status] || "Unknown";
    },

    getOrderStatusColor(status) {
      const colorMap = {
        0: "warning", 1: "info", 2: "info", 3: "info", 4: "info", 5: "secondary",
        6: "success", 7: "error", 8: "error", 9: "error", 10: "error", 11: "grey", 12: "error",
      };
      return colorMap[status] || "grey";
    },

    getProductName(oi) {
      return oi?.seller_product_variant?.product_variant?.product?.name || "Product";
    },

    getVariant(oi) {
      return oi?.seller_product_variant?.product_variant || null;
    },

    getTotalWeight(delivery) {
      if (!delivery.order || !delivery.order.order_items) return '—';
      const total = delivery.order.order_items.reduce((sum, oi) => {
        const w = parseFloat(oi.package_weight || (this.getVariant(oi) && this.getVariant(oi).weight) || 0);
        const qty = oi.quantity || 1;
        return sum + w * qty;
      }, 0);
      return total > 0 ? total.toFixed(0) : '—';
    },
  },
};
</script>

<style scoped>
@media (min-width: 960px) {
  .stat-col-5 {
    flex: 0 0 20% !important;
    max-width: 20% !important;
  }
}
</style>
